import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner } from '@bravemove/ui-web';
import type { Permission } from '@bravemove/types';
import { useAuth } from './useAuth';

export interface ProtectedRouteProps {
  /** Optional permission gate; missing → fall back to dashboard. */
  readonly permission?: Permission;
  readonly children: ReactNode;
}

/**
 * F06 — gate around any protected route. Renders a spinner while the auth
 * provider is restoring (`status === 'unknown'`), redirects to /login when
 * unauthenticated, and redirects to / when the actor lacks the required
 * permission (so the dashboard always remains an accessible safe surface).
 */
export function ProtectedRoute({ permission, children }: ProtectedRouteProps) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.status === 'unknown') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <Spinner size={32} />
      </div>
    );
  }

  if (auth.status === 'unauthenticated' || !auth.adminUser) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (permission && !auth.adminUser.permissions.includes(permission)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
